import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export interface PestControlPoint {
  id: string;
  name: string;
  icon: string;
  description: string | null;
  location: string | null;
  pointType: string | null;
  frequencyDays: number;
  warningDays: number;
  isActive: boolean;
  tenantId: string;
  createdAt: string;
}

export interface PestControlPointInactivityPeriod {
  id: string;
  pestControlPointId: string;
  startedAt: string;
  endedAt: string | null;
  label: string | null;
}

export type PestControlStatus = 'OVERDUE' | 'DUE_SOON' | 'OK';

export interface PestControlRecord {
  id: string;
  pestControlPointId: string;
  pointName: string;
  date: string;
  completedAt: string;
  pestDetected: boolean;
  notes: string | null;
  performedBy: string | null;
  performerName: string;
}

type PointDayStatus = 'checked' | 'due' | 'none';

export interface PointDayEntry {
  status: PointDayStatus;
}

interface PointMetadata {
  name: string;
  icon: string;
}

interface PestControlCalendarResponse {
  calendar: Record<string, Record<string, PointDayEntry>>;
  pointsMetadata: Record<string, PointMetadata>;
  /** Points due or checked that day, and the checked ones. */
  days: Record<string, { scheduledCount: number; completedCount: number }>;
}

export interface CreatePestControlRecordRequest {
  date: string;
  pestDetected?: boolean;
  notes?: string;
  signatureImage?: string;
}

export interface CorrectPestControlRecordRequest {
  pestDetected: boolean;
  notes?: string;
  reason?: string;
}

export interface CreatePestControlPointRequest {
  name: string;
  icon: string;
  description?: string;
  location?: string;
  pointType?: string;
  frequencyDays: number;
  warningDays?: number;
  isActive?: boolean;
}

export interface UpdatePestControlPointRequest {
  name?: string;
  icon?: string;
  description?: string;
  location?: string;
  pointType?: string;
  frequencyDays?: number;
  warningDays?: number;
}

export interface IPestControlService {
  findAll(): Promise<PestControlPoint[]>;
  findActive(): Promise<PestControlPoint[]>;
  getCalendar(from: string, to: string): Promise<PestControlCalendarResponse>;
  create(data: CreatePestControlPointRequest): Promise<PestControlPoint>;
  update(id: string, data: UpdatePestControlPointRequest): Promise<PestControlPoint>;
  deletePoint(id: string): Promise<void>;
  createRecord(pointId: string, data: CreatePestControlRecordRequest): Promise<PestControlRecord>;
  correctRecord(
    recordId: string,
    data: CorrectPestControlRecordRequest,
  ): Promise<PestControlRecord>;
  findRecordsByDateRange(from: string, to: string): Promise<PestControlRecord[]>;
  setActiveState(
    id: string,
    data: { active: boolean; label?: string | null },
  ): Promise<PestControlPointInactivityPeriod | null>;
  listInactivityPeriods(id: string): Promise<PestControlPointInactivityPeriod[]>;
  updateCurrentPeriodLabel(
    id: string,
    label: string | null,
  ): Promise<PestControlPointInactivityPeriod>;
}

@Injectable()
export class PestControlService implements IPestControlService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async findAll(): Promise<PestControlPoint[]> {
    return this.http.get<PestControlPoint[]>('pest-control');
  }

  async findActive(): Promise<PestControlPoint[]> {
    return this.http.get<PestControlPoint[]>('pest-control/active');
  }

  async create(data: CreatePestControlPointRequest): Promise<PestControlPoint> {
    return this.http.post<PestControlPoint>('pest-control', data);
  }

  async update(id: string, data: UpdatePestControlPointRequest): Promise<PestControlPoint> {
    return this.http.put<PestControlPoint>(`pest-control/${id}`, data);
  }

  async deletePoint(id: string): Promise<void> {
    await this.http.delete<void>(`pest-control/${id}`);
  }

  async getCalendar(from: string, to: string): Promise<PestControlCalendarResponse> {
    return this.http.get<PestControlCalendarResponse>(
      `pest-control/point-calendar?from=${from}&to=${to}`,
    );
  }

  async createRecord(
    pointId: string,
    data: CreatePestControlRecordRequest,
  ): Promise<PestControlRecord> {
    return this.http.post<PestControlRecord>(`pest-control/${pointId}/record`, data);
  }

  async correctRecord(
    recordId: string,
    data: CorrectPestControlRecordRequest,
  ): Promise<PestControlRecord> {
    return this.http.post<PestControlRecord>(`pest-control/records/${recordId}/corrections`, data);
  }

  async findRecordsByDateRange(from: string, to: string): Promise<PestControlRecord[]> {
    return this.http.get<PestControlRecord[]>(`pest-control/records?from=${from}&to=${to}`);
  }

  async setActiveState(
    id: string,
    data: { active: boolean; label?: string | null },
  ): Promise<PestControlPointInactivityPeriod | null> {
    return this.http.patch<PestControlPointInactivityPeriod | null>(
      `pest-control/${id}/active-state`,
      data,
    );
  }

  async listInactivityPeriods(id: string): Promise<PestControlPointInactivityPeriod[]> {
    return this.http.get<PestControlPointInactivityPeriod[]>(
      `pest-control/${id}/inactivity-periods`,
    );
  }

  async updateCurrentPeriodLabel(
    id: string,
    label: string | null,
  ): Promise<PestControlPointInactivityPeriod> {
    return this.http.patch<PestControlPointInactivityPeriod>(
      `pest-control/${id}/inactivity-periods/current`,
      { label },
    );
  }
}
